import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import UserService from "../../services/UserService";
import { GetEmployeeResponseDto } from "../../services/dtos/response/UserResponseDto";

const MAX_CHARACTERS = 200;

export const useQRLandingData = () => {
  const userService = useMemo(() => new UserService(), []);
  const [employee, setEmployee] = useState<GetEmployeeResponseDto | null>(null);
  const [rating, setRating] = useState<number | null>(null);
  const [animatedRating, setAnimatedRating] = useState<number>(0);
  const [isAnimating, setIsAnimating] = useState(true);
  const [comment, setComment] = useState<string | null>(null);
  const [error, setError] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const userId = params.get("userId");
  const branchId = params.get("branchId");

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isAnimating && animatedRating < 5 && employee) {
      timer = setTimeout(() => {
        setAnimatedRating((prev) => Math.min(prev + 0.2, 5));
      }, 25);
    }

    if (animatedRating === 5) {
      setTimeout(() => {
        setAnimatedRating(0);
        setIsAnimating(false);
      }, 300);
    }
    return () => clearTimeout(timer);
  }, [animatedRating, isAnimating, employee]);

  const handleRatingChange = useCallback(
    (event: React.ChangeEvent<{}>, newRating: number | null) => {
      setRating(newRating);
      setAnimatedRating(newRating || 0);
      setError({
        comment: "",
      });
    },
    []
  );

  const handleRatingChangeActive = useCallback(
    (event: React.SyntheticEvent<Element, Event>, value: number | null) => {
      if (value === rating) {
        event.preventDefault();
      }
    },
    [rating]
  );

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchEmployee = async () => {
      setIsLoading(true);

      try {
        await userService.getEmployee(
          {
            userid: +userId!,
            branchid: +branchId!,
          },
          (data) => {
            setEmployee(data);
          },
          (error) => {
            setError({ load: error });
          }
        );
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    if (userId && branchId) {
      fetchEmployee();
    }
  }, [userService, branchId, userId]);

  return {
    employee,
    rating,
    animatedRating,
    isAnimating,
    comment,
    error,
    isLoading,
    userId,
    branchId,
    setComment,
    setError,
    handleRatingChange,
    handleRatingChangeActive,
    MAX_CHARACTERS,
  };
};

